/**
 * Workflow Drafts Hook (Repository Pattern Compliant)
 *
 * React hook wrapping WorkflowDraftRepository for draft management.
 * Follows FERP architectural mandate: Repository pattern for ALL data access.
 *
 * Architecture:
 * - Repository Pattern: All GraphQL access through WorkflowDraftRepository
 * - Audit Trails: Complete logging via BaseRepository
 * - Safety Validation: Life-critical operation validation
 * - Domain-Agnostic: No hardcoded business logic
 *
 * Life-Critical: Production-ready with complete error handling
 * Compliance: HIPAA/SOX/FDA audit trail requirements
 *
 * @see CLAUDE.md Line 24: "Repository pattern mandatory - All data access through repositories"
 * @see WorkflowDraftRepository.ts for repository implementation
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import WorkflowDraftRepository, { type DraftRecord, type DraftVersion } from '../data/repositories/WorkflowDraftRepository';
import { useCurrentTenant } from './useCurrentTenant';

export interface UseWorkflowDraftsOptions {
  /** Filter by draft kind (e.g., 'WorkflowDefinition', 'Instance') */
  kind?: string;
  /** Auto-refresh interval in milliseconds (0 = disabled) */
  refreshInterval?: number;
  /** Enable auto-refresh (default: false) */
  enableAutoRefresh?: boolean;
}

export interface UseWorkflowDraftsReturn {
  /** List of draft records */
  drafts: DraftRecord[];
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Refresh drafts manually */
  refresh: () => Promise<void>;
  /** Delete a draft by key */
  deleteDraft: (key: string) => Promise<boolean>;
  /** Create a new draft */
  createDraft: (key: string, data: Record<string, any>, owner?: string) => Promise<DraftRecord | null>;
  /** Get version history for a draft */
  getVersionHistory: (key: string, limit?: number) => Promise<DraftVersion[]>;
  /** Repository instance (for advanced operations) */
  repository: WorkflowDraftRepository;
}

/**
 * Hook for accessing workflow drafts via Repository pattern
 *
 * @example
 * ```tsx
 * const { drafts, loading, error, refresh, deleteDraft } = useWorkflowDrafts({
 *   kind: 'WorkflowDefinition',
 *   enableAutoRefresh: true,
 *   refreshInterval: 30000, // 30 seconds
 * });
 * ```
 */
export function useWorkflowDrafts(
  options: UseWorkflowDraftsOptions = {}
): UseWorkflowDraftsReturn {
  const {
    kind,
    refreshInterval = 0,
    enableAutoRefresh = false,
  } = options;

  // Get tenant from context (data-driven architecture)
  const { tenantId: tenant } = useCurrentTenant();

  // Create repository instance (memoized to prevent recreating on every render)
  const repository = useMemo(
    () => new WorkflowDraftRepository(),
    []
  );

  // State management
  const [drafts, setDrafts] = useState<DraftRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load drafts from repository
   * Includes audit trail and safety validation (handled by repository)
   */
  const loadDrafts = useCallback(async () => {
    if (!tenant) {
      setError(new Error('Tenant ID is required'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let result: DraftRecord[];

      if (kind === 'WorkflowDefinition') {
        // Use specialized method for workflow definitions
        result = await repository.listWorkflowDefinitions(tenant);
      } else if (kind === 'Instance') {
        // Use specialized method for workflow instances
        result = await repository.listWorkflowInstances(tenant);
      } else if (kind) {
        // Use generic method with kind filter
        result = await repository.listDrafts(tenant, kind);
      } else {
        // No kind specified - this would require a new repository method
        // For now, default to WorkflowDefinition
        result = await repository.listWorkflowDefinitions(tenant);
      }

      // Filter out deleted drafts
      const activeDrafts = result.filter(draft => !draft.deleted);
      setDrafts(activeDrafts);
      setError(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[useWorkflowDrafts] Failed to load drafts:', error);
      setError(error);
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [tenant, kind, repository]);

  /**
   * Refresh drafts manually
   */
  const refresh = useCallback(async () => {
    await loadDrafts();
  }, [loadDrafts]);

  /**
   * Delete a draft by key
   * Audit trail logged by repository
   */
  const deleteDraft = useCallback(
    async (key: string): Promise<boolean> => {
      if (!tenant || !kind) {
        throw new Error('Tenant and kind are required to delete draft');
      }

      try {
        // Repository handles audit trail and safety validation
        const deleted = await repository.deleteDraft(tenant, kind, key);

        if (deleted) {
          // Refresh list after successful deletion
          await loadDrafts();
        }

        return deleted;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[useWorkflowDrafts] Failed to delete draft:', error);
        setError(error);
        throw error;
      }
    },
    [tenant, kind, repository, loadDrafts]
  );

  /**
   * Create a new draft
   * Audit trail logged by repository
   */
  const createDraft = useCallback(
    async (
      key: string,
      data: Record<string, any>,
      owner?: string
    ): Promise<DraftRecord | null> => {
      if (!tenant || !kind) {
        throw new Error('Tenant and kind are required to create draft');
      }

      try {
        const draft = await repository.createDraft({
          tenant,
          kind,
          key,
          data,
          owner,
        });

        // Refresh list after successful creation
        await loadDrafts();

        return draft;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[useWorkflowDrafts] Failed to create draft:', error);
        setError(error);
        throw error;
      }
    },
    [tenant, kind, repository, loadDrafts]
  );

  /**
   * Get version history for a draft
   * Audit trail logged by repository
   */
  const getVersionHistory = useCallback(
    async (key: string, limit?: number): Promise<DraftVersion[]> => {
      if (!tenant || !kind) {
        throw new Error('Tenant and kind are required to get version history');
      }

      try {
        // Repository handles audit trail and safety validation
        const versions = await repository.getDraftVersions(tenant, kind, key, limit);
        return versions;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[useWorkflowDrafts] Failed to get version history:', error);
        setError(error);
        throw error;
      }
    },
    [tenant, kind, repository]
  );

  // Initial load on mount or when dependencies change
  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  // Auto-refresh interval
  useEffect(() => {
    if (!enableAutoRefresh || refreshInterval <= 0) {
      return undefined;
    }

    const interval = setInterval(() => {
      loadDrafts();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [enableAutoRefresh, refreshInterval, loadDrafts]);

  return {
    drafts,
    loading,
    error,
    refresh,
    deleteDraft,
    createDraft,
    getVersionHistory,
    repository,
  };
}
